import React from "react";
import { Row, Col, Typography, Space, Card, Skeleton, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  selectProcessedInsightsData,
  selectWhatsappBusinessInsightsLoading,
} from "reduxToolkit/channelSlice";
import { InfoCircleFilled } from "@ant-design/icons";

import { useAspSelector } from "../../../../../test/jest-redux-hooks";

const { Title, Text } = Typography;

const CardSkeleton = () => (
  <Card
    styles={{
      body: { padding: "16px" },
      header: { border: "none" },
    }}
    style={{ height: "100%" }}
  >
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Space>
        <Skeleton.Button active />
        <Skeleton.Input active size="medium" />
      </Space>
      {[1, 2, 3, 4].map((index) => (
        <Row
          key={index}
          justify="space-between"
          align="middle"
          style={{ width: "100%" }}
        >
          <Skeleton.Button active />
          <Skeleton.Input active size="medium" />
        </Row>
      ))}
    </Space>
  </Card>
);

const Overview = () => {
  const { currentAccount } = useAspSelector((state) => state.app);

  const { whatsappBusinessInsightsLoading } = useSelector(
    (store) => store.Channel,
  );
  const processedData = useSelector(selectProcessedInsightsData);

  const formatData = () => {
    if (!processedData) return [];

    return [
      {
        title: "All conversations",
        value: processedData.allConversations.total,
        color: "bg-[#5C3BBF]",
        subMetrics: [
          {
            label: "Marketing",
            value: processedData.allConversations.marketing,
            color: "#32CDCD",
          },
          {
            label: "Utility",
            value: processedData.allConversations.utility,
            color: "#AC2F82",
          },
          {
            label: "Authentication",
            value: processedData.allConversations.authentication,
            color: "#F96056",
          },
          {
            label: "Service",
            value: processedData.allConversations.service,
            color: "#0ACD95",
          },
        ],
      },
      {
        title: "Free Conversations",
        value: processedData.freeConversations.total,
        color: "bg-[#5C3BBF]",
        subMetrics: [
          {
            label: "Free tier",
            value: processedData.freeConversations.freeTier,
            color: "#32CDCD",
          },
          {
            label: "Free entry point",
            value: processedData.freeConversations.freeEntryPoint,
            color: "#AC2F82",
          },
        ],
      },
      {
        title: "Paid conversations",
        value: processedData.paidConversations.total,
        color: "bg-[#5C3BBF]",
        subMetrics: [
          {
            label: "Marketing",
            value: processedData.paidConversations.marketing,
            color: "#32CDCD",
          },
          {
            label: "Utility",
            value: processedData.paidConversations.utility,
            color: "#AC2F82",
          },
          {
            label: "Authentication",
            value: processedData.paidConversations.authentication,
            color: "#F96056",
          },
          {
            label: "Service",
            value: processedData.paidConversations.service,
            color: "#0ACD95",
          },
        ],
      },
      {
        title: "Charges",
        value: `₹ ${processedData.charges.total.toFixed(2)}`,
        color: "bg-[#5C3BBF]",
        subMetrics: [
          {
            label: "Marketing",
            value: `₹ ${processedData.charges.marketing.toFixed(2)}`,
            color: "#32CDCD",
          },
          {
            label: "Utility",
            value: `₹ ${processedData.charges.utility.toFixed(2)}`,
            color: "#AC2F82",
          },
          {
            label: "Authentication",
            value: `₹ ${processedData.charges.authentication.toFixed(2)}`,
            color: "#F96056",
          },
          {
            label: "Service",
            value: `₹ ${processedData.charges.service.toFixed(2)}`,
            color: "#0ACD95",
          },
        ],
      },
    ];
  };

  const data = formatData();

  return (
    <div className="py-6">
      <Title level={5} className="mb-4">
        Overview
      </Title>
      <Row gutter={[24, 24]}>
        {whatsappBusinessInsightsLoading
          ? Array(4)
              .fill(null)
              .map((_, index) => (
                <Col key={index} xs={24} sm={12} md={12} lg={6}>
                  <CardSkeleton />
                </Col>
              ))
          : data.map((card, index) => (
            // Exclude the "Charges" card from rendering
              <>
                { (currentAccount?.role === "Account User" && currentAccount?.account_type === "Agency" && card.title === "Charges" )  ? ( 
                  <div /> 
                ) : ( 
                  <Col key={index} xs={24} sm={12} md={12} lg={ (currentAccount?.role === "Account User" && currentAccount?.account_type === "Agency") ? 8 : 6 }>
                    <Card
                    styles={{
                      body: { padding: "16px" },
                      header: { border: "none" },
                    }}
                    style={{ height: "100%" }}
                  >
                    <Row justify="space-between" align="middle" className="mb-4">
                      <Space align="center">
                        <span
                          className={`inline-block size-2 ${card.color} rounded-sm`}
                        />
                        <Text strong>{card.title}</Text>
                        {card.title === "Charges" && (
                          <Tooltip
                            title="lorem ipsum dolor sit amet"
                            color="white"
                            styles={{ body: { color: "black" } }}
                          >
                            <InfoCircleFilled className="text-[13px] text-gray-400" />
                          </Tooltip>
                        )}
                      </Space>
                      <Text strong className="text-sm">
                        {card.value}
                      </Text>
                    </Row>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      {card.subMetrics.map((metric, subIndex) => (
                        <Row
                          key={subIndex}
                          justify="space-between"
                          align="middle"
                          style={{ width: "100%" }}
                        >
                          <Space align="center">
                            <span
                              style={{ backgroundColor: metric.color }}
                              className={`inline-block size-2 ${metric.color} rounded-sm`}
                            />
                            <Text type="secondary">{metric.label}</Text>
                          </Space>
                          <Text>{metric.value}</Text>
                        </Row>
                      ))}
                    </Space>
                    </Card>
                  </Col>
                )}
              </>
            ))}
      </Row>
    </div>
  );
};

export default Overview;
